import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBBadge, MDBBtn, MDBCollapse, MDBIcon } from "mdbreact";
import swal from "sweetalert2";
import {
  DESTROY,
  SetEDIT,
  SetTAG,
  UPDATE,
} from "../../../../../services/redux/slices/market/machines";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatSnapshotTrail,
  getCalibrationStatus,
  getNextPmDate,
  getPmLabel,
  getWarrantyStatus,
  modeMeta,
} from "./utils";

const badge = (tone, label) => <MDBBadge color={tone}>{label}</MDBBadge>;
const getLatestTaggedBranch = (item = {}) => {
  const snapshots = Array.isArray(item?.snapshots) ? item.snapshots : [];
  const latestWithBranch = [...snapshots]
    .reverse()
    .find((snapshot) => snapshot?.toBranch);

  return item?.branch || latestWithBranch?.toBranch || {};
};

const branchLabel = (branch = {}) => {
  const companyName = branch?.companyId?.name || "";
  const branchName = branch?.name || branch?.displayname || "";

  if (companyName && branchName) return `${companyName} -> ${branchName}`;
  return branchName || "Unassigned";
};

const companyLabel = (branch = {}) =>
  branch?.companyId?.name || branch?.companyName || "Unassigned";

const branchNameLabel = (branch = {}) =>
  branch?.name || branch?.displayname || "Unassigned";

export default function MachineTable({ mode }) {
  const dispatch = useDispatch();
  const { token } = useSelector(({ auth }) => auth);
  const { filtered = [], activePage, maxPage } = useSelector(
    ({ machines }) => machines
  );
  const [openRow, setOpenRow] = useState(-1);

  const paginated = useMemo(() => {
    const start = (activePage - 1) * maxPage;
    return filtered.slice(start, start + maxPage);
  }, [activePage, filtered, maxPage]);

  const handleDelete = async (_id) => {
    const result = await swal.fire({
      title: "Delete machine?",
      text: "This will archive the supplier machine record.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
    });

    if (result.isConfirmed) {
      dispatch(DESTROY({ data: { _id }, token }));
    }
  };

  const handleUntag = async (item) => {
    const result = await swal.fire({
      title: "Untag machine?",
      text: "This will remove the tagged company/branch and return the machine to stock.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, untag it",
    });

    if (result.isConfirmed) {
      dispatch(
        UPDATE({
          data: {
            _id: item?._id,
            branch: null,
          },
          token,
        })
      );
    }
  };

  const meta = modeMeta[mode] || modeMeta.registry;

  if (!paginated.length) {
    return (
      <div className="text-center py-5 text-muted">
        <MDBIcon icon="microscope" size="2x" className="mb-3 d-block" />
        {meta.emptyText}
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-bordered">
        <thead className="blue lighten-5">
          <tr>
            <th>Machine</th>
            <th>Serial</th>
            {mode === "stock" && <th>Tagged Lab</th>}
            {mode === "tieup" && <th>Company</th>}
            {mode === "tieup" && <th>Branch</th>}
            {mode === "registry" && <th>Status</th>}
            {mode === "registry" && <th>Location</th>}
            {mode === "warranty" && <th>Warranty</th>}
            {mode === "warranty" && <th>Coverage</th>}
            {mode === "calibration" && <th>PM Interval</th>}
            {mode === "calibration" && <th>Next PM</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((item, index) => {
            const {
              _id,
              brand,
              model,
              serial,
              branch,
              status,
              location,
              warranty,
              lisCapable,
              lastPMDate,
              section,
              descriptions,
              price,
              acquired,
              conditionNotes,
            } = item;
            const actualIndex = (activePage - 1) * maxPage + index;
            const warrantyInfo = getWarrantyStatus(warranty);
            const calibrationInfo = getCalibrationStatus(item);
            const nextPmDate = getNextPmDate(item);
            const snapshots = formatSnapshotTrail(item?.snapshots);
            const taggedBranch = getLatestTaggedBranch(item);

            return (
              <FragmentRow
                key={_id || actualIndex}
                colSpan={mode === "registry" ? 5 : mode === "tieup" ? 6 : 5}
                isOpen={openRow === actualIndex}
                onToggle={() =>
                  setOpenRow((prev) => (prev === actualIndex ? -1 : actualIndex))
                }
                summary={
                  <tr>
                    <td>
                      <div className="font-weight-bold">{brand || "N/A"}</div>
                      <small className="text-muted">{model || "No model"}</small>
                    </td>
                    <td>{serial || "N/A"}</td>
                    {mode === "stock" && <td>{branchLabel(branch)}</td>}
                    {mode === "tieup" && <td>{companyLabel(taggedBranch)}</td>}
                    {mode === "tieup" && <td>{branchNameLabel(taggedBranch)}</td>}
                    {mode === "registry" && <td>{badge("info", status || "N/A")}</td>}
                    {mode === "registry" && <td>{location || section || "N/A"}</td>}
                    {mode === "warranty" && <td>{formatDate(warranty)}</td>}
                    {mode === "warranty" && (
                      <td>{badge(warrantyInfo.tone, warrantyInfo.label)}</td>
                    )}
                    {mode === "calibration" && <td>{getPmLabel(item.pm)}</td>}
                    {mode === "calibration" && (
                      <td>{nextPmDate ? formatDate(nextPmDate) : "N/A"}</td>
                    )}
                    <td className="text-nowrap">
                      {mode === "stock" && (
                        <MDBBtn
                          size="sm"
                          color="success"
                          rounded
                          className="mr-2"
                          onClick={() => dispatch(SetTAG(item))}
                        >
                          Tag
                        </MDBBtn>
                      )}
                      {mode !== "stock" && mode !== "tieup" && (
                        <MDBBtn
                          size="sm"
                          color="info"
                          rounded
                          className="mr-2"
                          onClick={() => dispatch(SetEDIT(item))}
                        >
                          Edit
                        </MDBBtn>
                      )}
                      {mode === "tieup" && (
                        <MDBBtn
                          size="sm"
                          color="warning"
                          rounded
                          onClick={() => handleUntag(item)}
                        >
                          Untag
                        </MDBBtn>
                      )}
                    </td>
                  </tr>
                }
                details={
                  <div className="p-3 bg-white">
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <div className="font-weight-bold">Asset details</div>
                        <div>Acquired: {acquired || "N/A"}</div>
                        <div>LIS capable: {lisCapable ? "Yes" : "No"}</div>
                        <div>Price: {formatCurrency(price)}</div>
                        {mode === "tieup" && (
                          <div>Tieup to: {branchLabel(taggedBranch)}</div>
                        )}
                      </div>
                      <div className="col-md-4 mb-3">
                        <div className="font-weight-bold">Service tracking</div>
                        <div>Last PM: {formatDate(lastPMDate)}</div>
                        <div>
                          Calibration:{" "}
                          {badge(calibrationInfo.tone, calibrationInfo.label)}
                        </div>
                        <div>
                          Warranty: {badge(warrantyInfo.tone, warrantyInfo.label)}
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <div className="font-weight-bold">Notes</div>
                        <div>{descriptions || "No description provided."}</div>
                        <div className="mt-2">
                          {conditionNotes || "No condition notes provided."}
                        </div>
                      </div>
                    </div>
                    <div className="pt-2 border-top">
                      <div className="font-weight-bold mb-2">Machine snapshots</div>
                      {snapshots.length ? (
                        snapshots.map((snapshot, snapshotIndex) => (
                          <div
                            key={`${_id}-snapshot-${snapshotIndex}`}
                            className="d-flex justify-content-between align-items-start mb-2"
                          >
                            <div>
                              <div className="font-weight-bold">
                                {snapshot.actionLabel}
                              </div>
                              <div>{snapshot.movement}</div>
                              {snapshot.remarks && (
                                <small className="text-muted d-block">
                                  {snapshot.remarks}
                                </small>
                              )}
                            </div>
                            <small className="text-muted text-right">
                              {formatDateTime(snapshot.at)}
                            </small>
                          </div>
                        ))
                      ) : (
                        <small className="text-muted">
                          No snapshot trail recorded yet.
                        </small>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                      <small className="text-muted">
                        Fixed asset is marked as `tieup` / supplier-owned.
                      </small>
                      <MDBBtn
                        size="sm"
                        color="danger"
                        rounded
                        onClick={() => handleDelete(_id)}
                      >
                        Delete
                      </MDBBtn>
                    </div>
                  </div>
                }
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ summary, details, isOpen, onToggle, colSpan }) {
  return (
    <>
      {summary}
      <tr>
        <td colSpan={colSpan} className="p-0 border-top-0">
          <div className="text-right pr-3 pt-2">
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={onToggle}
            >
              {isOpen ? "Hide details" : "View details"}
            </button>
          </div>
          <MDBCollapse isOpen={isOpen}>{details}</MDBCollapse>
        </td>
      </tr>
    </>
  );
}
